import { isUndefined, min } from 'lodash/fp';
import React, { useMemo } from 'react';
import classnames from 'classnames';

import { ARROW_WIDTH_OFFSET, Carousel } from '../../../shared/carousel/Carousel';
import { Agency, Bookshelf, Calculator, Form, OfficeBuilding, Robot, SmileyNeutral, ThirdParty, User } from '../../../shared/icons';
import { HomeButtonTile } from '../HomeButton';
import styles from '../Modules.module.scss';
import { PageUsageChart } from './PageUsageChart';
import { UsersChart } from './UsersChart';
import { useSystemColourPreference } from '../../../../hooks/useSystemColourPreference';
import { HomePageLink } from '../../../admin/system-maintenance/store';

const { lightestGrey } = styles;

const CAROUSEL_TIMEOUT = 15000; // 15 seconds
const TITLE_HEIGHT = 51;
const MODULE_PADDING = 20;

interface AdminModuleProps {
    dimensions?: {
        height: number;
        width: number;
    },
    analyticsSpinner: boolean;
    shouldRightAlignTitle: boolean;
    tileDisabledTooltip: (tile: HomePageLink) => string | undefined;
}

export const AdminModule: React.FC<AdminModuleProps> = ({ dimensions, analyticsSpinner, shouldRightAlignTitle, tileDisabledTooltip }) => {
    const showDarkTheme = useSystemColourPreference();
    const chartDimensions = useMemo(() => {
        if (isUndefined(dimensions)) {
            return { height: 0, width: 0 };
        }
        return { height: dimensions.height - TITLE_HEIGHT, width: (dimensions.width / 2) - (ARROW_WIDTH_OFFSET + MODULE_PADDING) };
    }, [dimensions]);

    const rotatingCharts = useMemo(() => [
        <PageUsageChart dimensions={chartDimensions} analyticsSpinner={analyticsSpinner} key='page-usage' />,
        <UsersChart dimensions={chartDimensions} analyticsSpinner={analyticsSpinner} key='user-roles' />
    ], [chartDimensions, analyticsSpinner]);

    const iconSize = useMemo(() => min([chartDimensions.height / 5, chartDimensions.width / 4]), [chartDimensions]);

    const chartDisplay = useMemo(() => {
        if (chartDimensions.height > chartDimensions.width * 3) {
            return rotatingCharts;
        }
        return (
            <Carousel
                markers={[0, 1]}
                height={chartDimensions.height}
                width={chartDimensions.width}
                timeout={CAROUSEL_TIMEOUT}
            >
                {rotatingCharts}
            </Carousel>
        );
    }, [rotatingCharts, chartDimensions]);

    return (
        <div className={styles.moduleWrapper}>
            <div className={classnames(styles.titleWrapper, { [styles.rightAlignTitle]: shouldRightAlignTitle, [styles.darkThemeTitleWrapper]: showDarkTheme })}>Admin</div>
            <div className={styles.contentWrapper}>
                <div className={styles.menuOptionsWrapper}>
                    <div className={styles.shortcutMenuOptions}>
                        <HomeButtonTile path='admin/users' label='Users' icon={User} testId='users' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_USERS))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_USERS)} />
                        <HomeButtonTile path='admin/roles' label='Roles' icon={ThirdParty} testId='roles' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_ROLES))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_ROLES)} />
                        <HomeButtonTile path='admin/access-groups' label='Access Groups' icon={Agency} testId='access-groups' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_ACCESS_GROUPS))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_ACCESS_GROUPS)} />
                        <HomeButtonTile path='admin/entities' label='Entities' icon={OfficeBuilding} testId='entities' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_ENTITIES))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_ENTITIES)} />
                        <HomeButtonTile path='admin/playbook' label='Playbooks' icon={Bookshelf} testId='playbooks' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_PLAYBOOKS))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_PLAYBOOKS)} />
                        <HomeButtonTile path='admin/intelligent-portfolio-analysis' label='Portfolio Analysis' icon={Robot} testId='portfolio-analysis' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_PORTFOLIO_ANALYSIS))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_PORTFOLIO_ANALYSIS)} />
                        <HomeButtonTile path='admin/risk-tolerance' label='Risk Tolerance' icon={SmileyNeutral} testId='risk-tolerance' iconSize={iconSize} fill={lightestGrey} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_RISK_TOLERANCE))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_RISK_TOLERANCE)} />
                        <HomeButtonTile path='admin/datasets' label='Datasets' icon={Form} testId='datasets' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_DATASETS))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_DATASETS)} />
                        <HomeButtonTile path='admin/calculator-prompts' label='Calculator Prompts' icon={Calculator} testId='calculator' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.ADMIN_CALCULATOR_PROMPTS))} disabledTooltip={tileDisabledTooltip(HomePageLink.ADMIN_CALCULATOR_PROMPTS)} />
                    </div>
                </div>
                <div className={styles.carouselChartsWrapper}>
                    {chartDisplay}
                </div>
            </div>
        </div>
    );
};
